import { motion } from 'framer-motion';
import { Diamond, BookOpen, Calendar, Clock, ArrowRight } from 'lucide-react';

const BlogPage = () => {
  // Placeholder blog posts - can be replaced with dynamic content later
  const blogPosts = [
    {
      id: 1,
      title: 'Coming Soon!',
      excerpt: 'Stay tuned for exciting blog posts about streaming tips, casino game strategies, community highlights, and behind-the-scenes content from RelatableShayla!',
      date: '',
      readTime: '',
      category: 'Announcement',
      featured: true
    }
  ];

  const categories = [
    'All Posts',
    'Streaming Tips',
    'Casino Games',
    'Community',
    'Behind the Scenes',
    'Announcements'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="blog-page">
      {/* Decorative blobs */}
      <div className="glow-blob glow-blob-purple top-40 -right-48 fixed" />
      <div className="glow-blob glow-blob-green bottom-40 -left-48 fixed" />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <motion.section
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mb-4">
            <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
            <span className="text-[#39FF14] font-medium uppercase tracking-wider text-sm">Blog</span>
            <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
          </motion.div>

          <motion.h1 
            variants={itemVariants}
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase mb-6"
          >
            <span className="text-[#E0E0E0]">The Relatable</span>
            <br />
            <span className="text-glow-purple text-[#9D4EDD]">Blog</span>
          </motion.h1>

          <motion.p 
            variants={itemVariants}
            className="text-lg text-gray-400 max-w-2xl mx-auto"
          >
            Insights, tips, and stories from my streaming journey. 
            Join me as I share what I've learned along the way!
          </motion.p>
        </motion.section>

        {/* Categories */}
        <motion.section
          className="mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div 
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-3"
          >
            {categories.map((category, index) => (
              <button
                key={category}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  index === 0
                    ? 'bg-[#9D4EDD] text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </motion.section>

        {/* Blog Posts */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {blogPosts.length === 1 && blogPosts[0].title === 'Coming Soon!' ? (
            // Placeholder state
            <motion.div 
              variants={itemVariants}
              className="glass-card p-12 text-center max-w-2xl mx-auto"
            >
              <div className="w-20 h-20 rounded-full bg-[#9D4EDD]/20 flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-[#9D4EDD]" />
              </div>
              <h2 className="text-2xl font-bold text-[#E0E0E0] mb-4">Blog Coming Soon!</h2>
              <p className="text-gray-400 mb-6">
                I'm working on some amazing content for you! Stay tuned for streaming tips, 
                casino game strategies, community highlights, and behind-the-scenes stories.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>New posts weekly</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>5-10 min reads</span>
                </div>
              </div>
            </motion.div>
          ) : (
            // Blog posts grid
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  variants={itemVariants}
                  className={`glass-card overflow-hidden group cursor-pointer ${
                    post.featured ? 'md:col-span-2 lg:col-span-2' : ''
                  }`}
                  data-testid={`blog-post-${index}`}
                >
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#9D4EDD]/20 text-[#9D4EDD]">
                        {post.category}
                      </span>
                      {post.date && (
                        <span className="text-xs text-gray-500">{post.date}</span>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-bold text-[#E0E0E0] mb-3 group-hover:text-[#9D4EDD] transition-colors">
                      {post.title}
                    </h3>
                    
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    
                    {post.readTime && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          <span>{post.readTime}</span>
                        </div>
                        <span className="text-[#9D4EDD] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read More <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </motion.section>

        {/* Newsletter Signup */}
        <motion.section
          className="mt-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div 
            variants={itemVariants}
            className="glass-card p-8 text-center"
          >
            <h2 className="text-xl font-bold text-[#E0E0E0] mb-4">Stay Updated!</h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-6">
              Join The Relatable Family on Discord to get notified when new blog posts drop!
            </p>
            <a
              href="https://discord.gg/FEwaZfwnN5"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary px-8 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2"
              data-testid="blog-discord-btn"
            >
              Join Discord for Updates
            </a>
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
};

export default BlogPage;
